// A curated note as it could have read on a date: the clauses that date something after it are
// dropped (a 2022-02-24 note cannot report the annexation of 30 Sep 2022).
//
//   'The area is the DeepState layer; it includes Crimea. Russia declared four oblasts annexed on 30 Sep 2022.'
//        on 2022-02-24 → 'The area is the DeepState layer; it includes Crimea.'
//
// A clause is a piece between ';' or '.'. It dates what its latest date says: 'D Mon YYYY' is that
// day, 'Mon YYYY' is any day of the month (kept when the month is D's), a bare year is its January.

import type { DateStr } from '../core/schema.ts';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_NAME = '(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*';
const CLAUSE_BREAK = /(?<=[.;])\s+/;
const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
const DAY_MONTH_YEAR = new RegExp(`\\b(\\d{1,2}) ${MONTH_NAME} (\\d{4})\\b`, 'g');
const MONTH_YEAR = new RegExp(`\\b${MONTH_NAME} (\\d{4})\\b`, 'g');
const BARE_YEAR = /\b(1[89]\d{2}|20\d{2})\b/g;
const MONTH_DIGITS = 2;
const DAY_DIGITS = 2;
const YEAR_LENGTH = 4;
const MONTH_LENGTH = 7;

/** 'YYYY-MM' of a month name and year. */
function monthOf(name: string, year: string): string {
  const n = MONTHS.indexOf(name.slice(0, 3).toLowerCase()) + 1;
  return `${year}-${String(n).padStart(MONTH_DIGITS, '0')}`;
}

/** Whether something in the clause is dated after `date`. */
function laterThan(clause: string, date: DateStr): boolean {
  const month = date.slice(0, MONTH_LENGTH);
  const year = date.slice(0, YEAR_LENGTH);
  const iso = [...clause.matchAll(ISO_DATE)];
  const days = [...clause.matchAll(DAY_MONTH_YEAR)];
  const months = [...clause.matchAll(MONTH_YEAR)];

  const later =
    iso.some((m) => m[0] > date) ||
    days.some((m) => `${monthOf(m[2], m[3])}-${m[1].padStart(DAY_DIGITS, '0')}` > date) ||
    months.some((m) => monthOf(m[1], m[2]) > month);
  if (later || iso.length + days.length + months.length > 0) {
    return later;
  }

  // A bare year counts only where nothing finer dates the clause.
  return [...clause.matchAll(BARE_YEAR)].some((m) => m[1] > year);
}

/** The note without the clauses dated after `date`; a ';' left at the end reads as a full stop. */
export function noteAsOf(note: string, date: DateStr): string {
  const kept = note.split(CLAUSE_BREAK).filter((clause) => !laterThan(clause, date));
  return kept.join(' ').replace(/;$/, '.');
}
